import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Eye } from 'react-bootstrap-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateButton } from '../../hooks/useCreateButton';
import { useCreateContext } from '../../contexts/CreateContext';
import useCodeCommon from '../../hooks/useCodeCommon';
import useCodeCommonByCategory from '../../hooks/useCodeCommonByCategory';
import useHubs from '../../hooks/useHubs';
import useRoleBasedFiltering from '../../hooks/useRoleBasedFiltering';
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../../components/ui/Pagination';
import SortableTable, { SortableColumn } from '../../components/common/SortableTable';
import SearchableSelectInput from '../../components/forms/SearchableSelectInput';
import CreateVehiclePage from './Create-Vehicle/CreateVehiclePage';  
import BulkUploadModal from '../../components/vehicle/BulkUploadModal';
import ActionIcon from "../../../src/assets/icons/ActionIcon.svg";
import EditIcon from "../../../src/assets/icons/EditIcon.svg";
import DeleteIcon from "../../../src/assets/icons/DeleteIcon.svg";
import apiService from '../../services/api';
import { DATE_FORMATS } from '../../constants';
import { useNotification } from '../../contexts/NotificationContext';
import { useAppSelector } from '../../hooks/useAuth';
import CustomLoading from '../../components/common/CustomLoading';
import { getHubId } from '../../utils/cookieUtils';

interface VehicleData {
  id: string;
  vehicleName: string;
  model: string;
  registrationNumber: string;
  status: string;
  range: string;
  topSpeed: string;
  assignedHub: string;
  hubmasterid?: string | number;
  vehicleNumber?: string;
  telematicsDeviceId?: string;
}
const StatusChip = ({ status, statusOptions }: { status: string; statusOptions: any[] }) => {
  if (!status) return null;

  const getStatusText = (status: string) => {
    const statusOption = statusOptions.find(option => option.codevalue === status || option.codedisplayname === status);
    return statusOption ? statusOption.codedisplayname : status;
  };

  const statusText = getStatusText(status);
  
  const styles: Record<string, any> = {
    Active: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
    Banned: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
    "Under Maintenance": { bg: "rgba(215, 215, 215, 1)", color: "rgba(60, 60, 60, 1)" },
    Booked: { bg: "rgba(255, 245, 157, 1)", color: "rgba(107, 105, 14, 1)" },
    Available: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
    Absconded: { bg: "rgba(255, 235, 205, 1)", color: "#FF3B30" },
    B2B: { bg: "rgba(237, 231, 255, 1)", color: "rgba(81, 45, 168, 1)" },
    Inactive: { bg: "rgba(255, 236, 209, 1)", color: "rgba(230, 81, 0, 1)" },
  };
  
  return (
    <Chip
      label={statusText}
      size="medium"
      sx={{ 
        backgroundColor: styles[statusText]?.bg || "rgba(245, 245, 245, 1)", 
        color: styles[statusText]?.color || "rgba(117, 117, 117, 1)", 
        fontWeight: 600 
      }}
    />
  );
};

const KycChip = ({ kyc }: { kyc: string }) => {
  const colors: Record<string, string> = {
    Verified: "rgba(31, 113, 13, 1)",
    Rejected: "rgba(197, 0, 0, 1)",
    Pending: "rgba(107, 105, 14, 1)",
  };
  return (
    <Chip
      label={kyc}
      size="medium"
      sx={{ backgroundColor: "transparent", color: colors[kyc], fontWeight: 600 }}
    />
  );
};

const FilterSelect = ({ label, value, onChange, options }: any) => (
  <FormControl size="small" sx={{ minWidth: '200px' }}>
    <InputLabel>{label}</InputLabel>
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <MenuItem value="">(Select)</MenuItem>
      {options.map((opt: string) => (
        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const VehicleManagementPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<VehicleData[]>([]);
  const [rawVehicles, setRawVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Restore state from location if available
  const getInitialModelFilter = () => (location.state as any)?.modelFilter || '';
  const getInitialHub = () => (location.state as any)?.hub || '';
  const getInitialStatus = () => (location.state as any)?.status || '';
  const getInitialVehicleNumber = () => (location.state as any)?.vehicleNumber || '';
  const getInitialRegistrationNumber = () => (location.state as any)?.registrationNumber || '';
  const getInitialTelematicsDeviceId = () => (location.state as any)?.telematicsDeviceId || '';
  const getInitialPage = () => (location.state as any)?.page || 0;

  const [modelFilter, setModelFilter] = useState(getInitialModelFilter());
  const [hub, setHub] = useState(getInitialHub());
  const [status, setStatus] = useState(getInitialStatus());
  const [vehicleNumber, setVehicleNumber] = useState(getInitialVehicleNumber());
  const [registrationNumber, setRegistrationNumber] = useState(getInitialRegistrationNumber());
  const [telematicsDeviceId, setTelematicsDeviceId] = useState(getInitialTelematicsDeviceId());
  const [hubManager, setHubManager] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { showCreateVehicle, setShowCreateVehicle, showBulkUploadVehicle, setShowBulkUploadVehicle } = useCreateContext();
  const { showNotification } = useNotification();
  
  const { options: allStatusOptions, loading: statusLoading, error: statusError } = useCodeCommon('VehicleStatus');
  
  // Use all status options instead of filtering to support all status types
  const statusOptions = allStatusOptions;
  
  const {
    statusCodes: vehicleModelCodes,
    statusOptions: vehicleModelOptions,
    loading: vehicleModelLoading,
    getDisplayNameByCodeValue: getVehicleModelDisplayName,
    getCodeValueByDisplayName: getVehicleModelCodeValueByDisplayName,
  } = useCodeCommonByCategory("VehicleModel");
  
  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const { filterByRole } = useRoleBasedFiltering<VehicleData>();
  const user = useAppSelector((state: any) => (state as any).auth?.user); // fix for error
  const isSuperAdmin = user?.roleid === 1;
  const hasEditDeletePermission = user?.roleid && [1, 2, 7, 10].includes(user.roleid);
  
  const { CreateButton: CreateVehicleButton } = useCreateButton({
    label: 'Create Vehicle',
    onClick: () => setShowCreateVehicle(true)
  });

  const [totalCount, setTotalCount] = useState(0);

  const pagination = usePagination({
    totalItems: totalCount,
    initialPage: getInitialPage(),
    initialRowsPerPage: 10,
  });

  const resolveHubMasterIdForApi = useCallback((): number => {
    if (!isSuperAdmin) {
      const hid = getHubId();
      return hid != null && Number.isFinite(hid) ? Number(hid) : 0;
    }
    if (hub) {
      const found = hubOptions.find((h) => h.hubname === hub);
      return found ? Number(found.hubmasterid) : 0;
    }
    return 0;
  }, [isSuperAdmin, hub, hubOptions]);

  const resolveStatusCodeForApi = useCallback((): number => {
    if (!status) return 0;
    const row = allStatusOptions.find((o) => o.codedisplayname === status);
    const n = Number.parseInt(String(row?.codevalue ?? "0"), 10);
    return Number.isFinite(n) ? n : 0;
  }, [status, allStatusOptions]);

  const resolveVehicleModelCodeForApi = useCallback((): number => {
    if (!modelFilter) return 0;
    const cv = getVehicleModelCodeValueByDisplayName(modelFilter);
    if (cv == null || cv === "") return 0;
    const n = Number.parseInt(String(cv), 10);
    return Number.isFinite(n) ? n : 0;
  }, [modelFilter, getVehicleModelCodeValueByDisplayName]);

  const parsePaginatedVehicleResponse = useCallback((response: unknown): { items: any[]; total: number } => {
    const root = (response as any)?.data !== undefined ? (response as any).data : response;
    if (root == null) {
      return { items: [], total: 0 };
    }
    if (Array.isArray(root)) {
      return { items: root, total: root.length };
    }
    if (typeof root !== "object") {
      return { items: [], total: 0 };
    }
    const r = root as Record<string, unknown>;
    const itemsRaw =
      (Array.isArray(r.vehicles) && r.vehicles) ||
      (Array.isArray(r.result) && r.result) ||
      (Array.isArray(r.data) && r.data) ||
      (Array.isArray(r.items) && r.items) ||
      [];
    const items = itemsRaw as any[];
    const totalCandidate =
      r.totalCount ?? r.totalRecords ?? r.total ?? r.totalRows ?? r.recordCount ?? r.totalItemCount;
    let total: number;
    if (typeof totalCandidate === "number" && Number.isFinite(totalCandidate)) {
      total = totalCandidate;
    } else if (totalCandidate != null && String(totalCandidate).trim() !== "") {
      const p = Number.parseInt(String(totalCandidate), 10);
      total = Number.isFinite(p) ? p : items.length;
    } else {
      total = items.length;
    }
    return { items, total };
  }, []);

  const displayedVehicles = filterByRole(vehicles, "hubmasterid");

  const columns: SortableColumn<VehicleData>[] = [
    { id: 'vehicleName', label: 'Vehicle Name' },
    { id: 'vehicleNumber', label: 'Vehicle Number' },
    { id: 'registrationNumber', label: 'Registration Number' },
    { id: 'model', label: 'Model' },
    { id: 'telematicsDeviceId', label: 'Telematics ID' },
    { id: 'assignedHub', label: 'Hub Name' },
    { id: 'topSpeed', label: 'Top Speed', align: 'center' },
    { id: 'range', label: 'Range', align: 'center' },
    { 
      id: 'status', 
      label: 'Status', 
      align: 'center',
      render: (row: VehicleData) => <StatusChip status={row.status} statusOptions={statusOptions} />
    },
    {
      id: 'actions' as keyof VehicleData,
      label: 'Actions',
      align: 'center',
      sortable: false,
      render: (row: VehicleData) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button 
            variant="text" 
            sx={{ p: 0, minWidth: "auto" }}
            onClick={() => handleViewVehicleDetails(row.id)}
          >
            <img src={ActionIcon} alt="Action" width={30} height={30} />
          </Button>
          {hasEditDeletePermission && (
            <Button 
              variant="text" 
              sx={{ p: 0, minWidth: "auto" }}
              onClick={() => handleEditVehicle(row.id)}
            >
              <img src={EditIcon} alt="Edit" width={30} height={30} />
            </Button>
          )}
          {hasEditDeletePermission && (
            <Button 
              variant="text" 
              sx={{ p: 0, minWidth: "auto" }}
              onClick={() => handleDeleteVehicle(row.id)}
            >
              <img src={DeleteIcon} alt="Delete" width={30} height={30} />
            </Button>
          )}
        </Box>
      )
    },
  ];

  const getVehicleModelNameByCodeValue = (vehiclemodelid: number | string | null | undefined): string => {
    if (vehiclemodelid === null || vehiclemodelid === undefined) return 'N/A';
    return getVehicleModelDisplayName(vehiclemodelid) || 'N/A';
  };

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        /** API uses 1-based page index; UI pagination is 0-based. */
        pageNumber: pagination.page + 1,
        pageSize: pagination.rowsPerPage,
        vehiclenum: vehicleNumber.trim(),
        registrationnumber: registrationNumber.trim(),
        telematicsid: telematicsDeviceId.trim(),
        vehiclemodel: resolveVehicleModelCodeForApi(),
        hubmasterid: resolveHubMasterIdForApi(),
        status: resolveStatusCodeForApi(),
      };

      const response = await apiService.getAllVehicles(payload);
      const { items, total } = parsePaginatedVehicleResponse(response);

      setTotalCount(total);
      setRawVehicles(items);
      if (items.length === 0) {
        setVehicles([]);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch vehicles");
      setTotalCount(0);
      setRawVehicles([]);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.rowsPerPage,
    vehicleNumber,
    registrationNumber,
    telematicsDeviceId,
    resolveVehicleModelCodeForApi,
    resolveHubMasterIdForApi,
    resolveStatusCodeForApi,
    parsePaginatedVehicleResponse,
  ]);

  useEffect(() => {
    void fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    const state = location.state as any;
    if (state?.refresh) {
      void fetchVehicles();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, fetchVehicles]);

  useEffect(() => {
    if (rawVehicles.length === 0) {
      setVehicles([]);
      return;
    }
    const vehicleData: VehicleData[] = rawVehicles.map((vehicle: any) => {
      const getStatusText = (statusVal: any) => {
        const statusOption = statusOptions.find((option) => option.codevalue === String(statusVal));
        return statusOption ? statusOption.codedisplayname : String(statusVal) || "Active";
      };

      const getHubName = (hubId: any) => {
        if (!hubId || !hubOptions || hubOptions.length === 0) return "N/A";
        const h = hubOptions.find((ho) => ho.hubmasterid === hubId);
        return h ? h.hubname : "N/A";
      };

      const vehicleModelName = getVehicleModelNameByCodeValue(vehicle.vehiclemodelid || vehicle.vehiclemodel);

      return {
        id: (vehicle.vehicleid || "N/A").toString(),
        vehicleName: vehicle.vehiclename || "N/A",
        model: vehicleModelName,
        registrationNumber: vehicle.registrationnumber || "N/A",
        status: getStatusText(vehicle.status),
        range: vehicle.rangeperchargekm || "N/A",
        topSpeed: vehicle.topspeed || "N/A",
        assignedHub: getHubName(vehicle.hubmasterid),
        hubmasterid: vehicle.hubmasterid,
        vehicleNumber: vehicle.vehiclenum || "N/A",
        telematicsDeviceId: vehicle.telematicsdeviceid || "N/A",
      };
    });

    setVehicles(vehicleData);
  }, [rawVehicles, hubOptions, statusOptions, vehicleModelCodes]);

  const handleViewVehicleDetails = async (vehicleId: string) => {
    try {
      setLoading(true);
      navigate(`/vehicle-management/vehicle-details/${vehicleId}`, {
        state: {
          vehicleNumber,
          telematicsDeviceId,
          modelFilter,
          hub,
          status,
          page: pagination.page,
        },
      });
    } catch (err: any) {
      setError(err.message || "Failed to navigate to vehicle details");
    } finally {
      setLoading(false);
    }
  };

  const handleEditVehicle = async (vehicleId: string) => {
    try {
      setLoading(true);
      navigate(`/vehicle-management/edit-vehicle/${vehicleId}`, {
        state: {
          vehicleNumber,
          telematicsDeviceId,
          modelFilter,
          hub,
          status,
          page: pagination.page,
        },
      });
    } catch (err: any) {
      setError(err.message || "Failed to navigate to edit vehicle");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = (vehicleId: string) => {
    setVehicleToDelete(vehicleId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!vehicleToDelete) return;
    
    setDeleting(true);
    try {
      await apiService.deleteVehicle(vehicleToDelete);
      await fetchVehicles();
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete vehicle");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setVehicleToDelete(null);
  };

  if (location.pathname === "/vehicle-management/create-vehicle") {
    return <CreateVehiclePage onBack={() => setShowCreateVehicle(false)} />;
  }

  return (
    <>
    <Container fluid className="px-0">
      {(error || statusError) && (
        <Alert 
          severity="error" 
          className="mb-3" 
          onClose={() => setError(null)}
        >
          {error || statusError}
        </Alert>
      )}

      <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
        <Row className="g-3 align-items-end">
          <Col xs={12} md={2}>
            <TextField
              label="Vehicle Number"
              placeholder="Enter vehicle number"
              size="small"
              fullWidth
              value={vehicleNumber}
              onChange={(e) => {
                const newVehicleNumber = e.target.value;
                setVehicleNumber(newVehicleNumber);
                pagination.goToPage(0);
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...(location.state as object || {}),
                    vehicleNumber: newVehicleNumber,
                    page: 0,
                  },
                });
              }}
            />
          </Col>

          <Col xs={12} md={2}>
            <TextField
              label="Registration Number"
              placeholder="Enter registration number"
              size="small"
              fullWidth
              value={registrationNumber}
              onChange={(e) => {
                const newRegistrationNumber = e.target.value;
                setRegistrationNumber(newRegistrationNumber);
                pagination.goToPage(0);
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...(location.state as object || {}),
                    registrationNumber: newRegistrationNumber,
                    page: 0,
                  },
                });
              }}
            />
          </Col>

          <Col xs={12} md={2}>
            <TextField
              label="Telematics ID"
              placeholder="Enter telematics ID"
              size="small"
              fullWidth
              value={telematicsDeviceId}
              onChange={(e) => {
                const newTelematicsDeviceId = e.target.value;
                setTelematicsDeviceId(newTelematicsDeviceId);
                pagination.goToPage(0);
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...(location.state as object || {}),
                    telematicsDeviceId: newTelematicsDeviceId,
                    page: 0,
                  },
                });
              }}
            />
          </Col>

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="modelFilter"
              formData={{ modelFilter }}
              onChange={(_, value) => {
                const newModelFilter = value;
                setModelFilter(newModelFilter);
                pagination.goToPage(0);
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...(location.state as object || {}),
                    modelFilter: newModelFilter,
                    page: 0,
                  },
                });
              }}
              options={vehicleModelOptions || []}
              label="Model"
              placeholder="Search and select model"
              disabled={vehicleModelLoading}
              loading={vehicleModelLoading}
            />
          </Col>

          {isSuperAdmin && (
            <Col xs={12} md={2}>
              <SearchableSelectInput
                field="hub"
                formData={{ hub }}
                onChange={(_, value) => {
                  setHub(value);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...(location.state as object || {}),
                      hub: value,
                      page: 0,
                    },
                  });
                }}
                options={hubsLoading ? [] : hubOptions.map(h => h.hubname)}
                label="Hub"
                placeholder="Search and select hub"
                disabled={hubsLoading}
              />
            </Col>
          )}

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="status"
              formData={{ status }}
              onChange={(_, value) => {
                setStatus(value);
                pagination.goToPage(0);
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...(location.state as object || {}),
                    status: value,
                    page: 0,
                  },
                });
              }}
              options={statusOptions.map((option) => option.codedisplayname)}
              label="Status"
              placeholder="Search and select status"
              disabled={statusLoading}
            />
          </Col>

          <Col xs={12} md={4} className="d-flex gap-2 justify-content-end" />
        </Row>
      </Paper>

      <Paper className="border-0 rounded-0 shadow-none">
        {(loading || statusLoading || hubsLoading || vehicleModelLoading) ? (
          <CustomLoading message="Fetching Vehicle Table Data..." />
        ) : (
          <SortableTable
            data={displayedVehicles}
            columns={columns}
            emptyMessage="No vehicles found"
          />
        )}
      </Paper>
    </Container>
    <Pagination
      page={pagination.page}
      rowsPerPage={pagination.rowsPerPage}
      totalItems={totalCount}
      totalPages={pagination.totalPages}
      startItem={pagination.startItem}
      endItem={pagination.endItem}
      rowsPerPageOptions={[5, 10, 25, 50]}
      showRowsPerPage
       onRowsPerPageChange={pagination.handleChangeRowsPerPage}
       onGoToNextPage={pagination.goToNextPage}
       onGoToPreviousPage={pagination.goToPreviousPage}
    />

    <Dialog
      open={deleteDialogOpen}
      onClose={cancelDelete}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
    >
      <DialogTitle id="delete-dialog-title">
        Confirm Delete
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-dialog-description">
          Are you sure you want to delete this vehicle? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={cancelDelete} 
          disabled={deleting}
          sx={{ color: "rgba(13, 44, 90, 1)" }}
        >
          Cancel
        </Button>
        <Button 
          onClick={confirmDelete} 
          disabled={deleting}
          variant="contained"
          sx={{ 
            backgroundColor: "rgba(197, 0, 0, 1)",
            "&:hover": { backgroundColor: "rgba(197, 0, 0, 0.8)" }
          }}
        >
          {deleting ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
        </Button>
      </DialogActions>
    </Dialog>
     <BulkUploadModal
      open={showBulkUploadVehicle}
      onClose={() => setShowBulkUploadVehicle(false)}
      onUploadSuccess={() => {
        fetchVehicles(); // Refetch vehicles after a successful upload
        setShowBulkUploadVehicle(false); // Close the modal on success
        showNotification('File uploaded successfully');
      }}
    />
  </>
  );
};

export default VehicleManagementPage;
